import { useTranslation } from "react-i18next"
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"
import type { HttpRequestInputContract } from "@/types/httpRequestTool"
import {
  collectHttpAgentInputs,
  getHttpInputContractErrors,
  type HttpAgentInput,
} from "@/lib/httpRequestInputs"

const prefix = "workspace.agent.basicForm.unifiedToolSelector.httpRequestInputs"
const formats: Record<string, HttpRequestInputContract> = {
  string: { type: "string" },
  jsonObject: { type: "string", format: "json", jsonType: "object" },
  jsonArray: { type: "string", format: "json", jsonType: "array" },
  jsonAny: { type: "string", format: "json", jsonType: "any" },
  number: { type: "number" },
  integer: { type: "integer" },
  boolean: { type: "boolean" },
  object: { type: "object" },
  array: { type: "array" },
}

export interface HttpRequestInputFormatSelectProps {
  field: HttpAgentInput
  contract?: HttpRequestInputContract
  disabled?: boolean
  onChange: (contract: HttpRequestInputContract) => void
}

export const HttpRequestInputFormatSelect = ({
  field,
  contract = {},
  disabled,
  onChange,
}: HttpRequestInputFormatSelectProps) => {
  const { t } = useTranslation()
  const value =
    contract.format === "json"
      ? { object: "jsonObject", array: "jsonArray", any: "jsonAny" }[
          contract.jsonType ?? "any"
        ]
      : (contract.type ?? "string")
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(value) => onChange(formats[value])}
    >
      <SelectTrigger
        className="w-full min-w-0"
        aria-label={`${field.key}: ${t(`${prefix}.type`)}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.keys(formats).map((option) => (
          <SelectItem
            key={option}
            value={option}
            disabled={!field.wholeValue && option !== "string"}
          >
            {t(`${prefix}.types.${option}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export interface HttpRequestInputContractsProps {
  template: unknown
  contracts?: Record<string, HttpRequestInputContract>
  disabled?: boolean
  showFields: boolean
  onChange: (contracts: Record<string, HttpRequestInputContract>) => void
}

export const HttpRequestInputContracts = ({
  template,
  contracts = {},
  disabled,
  showFields,
  onChange,
}: HttpRequestInputContractsProps) => {
  const { t } = useTranslation()
  const fields = collectHttpAgentInputs(template)
  const errors = getHttpInputContractErrors(template, contracts)
  const hasJson = fields.some(
    (field) => contracts[field.key]?.format === "json"
  )
  if (!fields.length && !errors.length) return null
  const remove = (key: string) => {
    const next = { ...contracts }
    delete next[key]
    onChange(next)
  }
  return (
    <div className="space-y-2">
      {showFields && fields.length > 0 && (
        <section className="space-y-2" aria-label={t(`${prefix}.title`)}>
          <h3 className="text-sm font-medium">{t(`${prefix}.title`)}</h3>
          {fields.map((field) => (
            <div
              key={field.key}
              className="grid grid-cols-[minmax(0,1fr)_10rem] items-center gap-2"
            >
              <span className="break-all text-sm">{field.path}</span>
              <HttpRequestInputFormatSelect
                field={field}
                contract={contracts[field.key]}
                disabled={disabled}
                onChange={(contract) =>
                  onChange({ ...contracts, [field.key]: contract })
                }
              />
            </div>
          ))}
        </section>
      )}
      {hasJson && (
        <p className="text-xs text-muted-foreground">
          {t(`${prefix}.jsonHelp`)}
        </p>
      )}
      {errors.map(({ key, reason }) => (
        <div
          key={key}
          role="alert"
          className="flex items-center justify-between gap-2 text-sm text-destructive"
        >
          <p>{t(`${prefix}.errors.${reason}`, { key })}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={() => remove(key)}
          >
            {t(`${prefix}.reset`)}
          </Button>
        </div>
      ))}
    </div>
  )
}
